// Reading the coordination contract out loud, in Spanish, without rounding off
// its edges.
//
// THE RULES THESE HELPERS EXIST TO KEEP:
//
//   · `null` is not zero. A duration that is not known is drawn as «no se
//     sabe» with the reason the contract gave, and never as `0` nor as a dash
//     that could pass for one.
//   · Validity is three words (`vigente`, `vencida`, `no_vence`), not a
//     boolean.
//   · Nothing is called «productividad»: the contract forbids the word, and the
//     numbers are distances between two server marks.
//   · An unknown label is printed as it came. Inventing a nicer Spanish word
//     for a value the backend may add tomorrow hides that it is new.

export const UNKNOWN = Symbol('desconocido');

// ---- duraciones ----

// Seconds, as the contract sends them: a number with millisecond precision, or
// `null` with a reason. Returns `{ known, text, reason }` — never a bare 0.
export function readSeconds(span) {
    if (span == null) return { known: false, text: 'no se sabe', reason: null };
    const value = typeof span === 'number' ? span : span.seconds;
    const reason = typeof span === 'number' ? null : (span.reason ?? null);
    if (value == null) return { known: false, text: 'no se sabe', reason };
    return { known: true, text: humanSeconds(value), reason: null };
}

// 0.063 → «63 ms»; 91 → «1 min 31 s»; 7199 → «2 h 0 min». The exact number
// travels next to it in a tooltip, because rounding a measure somebody may
// want to re-derive is how a number stops being checkable.
export function humanSeconds(value) {
    if (value == null) return 'no se sabe';
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return 'no se sabe';
    if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
    if (seconds < 60) return `${trim(seconds)} s`;
    if (seconds < 3600) {
        const rounded = Math.round(seconds);
        return `${Math.floor(rounded / 60)} min ${rounded % 60} s`;
    }
    const minutes = Math.round(seconds / 60);
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

function trim(value) {
    return String(Math.round(value * 1000) / 1000);
}

export const UNKNOWN_REASON_LABEL = {
    sin_inicio: 'sin inicio',
    sin_fin: 'sin fin',
    fuera_de_orden: 'fuera de orden',
    en_curso: 'en curso',
    sin_entrega: 'sin entrega',
    sin_reentrega: 'sin reentrega',
    no_devuelta: 'no devuelta',
};

export function unknownLabel(reason) {
    return UNKNOWN_REASON_LABEL[reason] ?? reason;
}

// The unknown block of an aggregation: `{ en_curso: 2 }` → «2 en curso».
export function unknownBreakdown(unknown) {
    const entries = Object.entries(unknown ?? {});
    if (!entries.length) return null;
    return entries.map(([reason, count]) => `${count} ${unknownLabel(reason)}`).join(' · ');
}

// ---- horas ----

export function dateTime(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('es', { dateStyle: 'short', timeStyle: 'medium' });
}

// «hace 10 min», against the `observed_at` of the answer and not against the
// browser's clock: the fixtures were captured on a day that is not today, and
// measuring them against now would report hours of staleness that nobody has.
export function ageFrom(observedAt, value) {
    if (!value || !observedAt) return null;
    const seconds = (new Date(observedAt).getTime() - new Date(value).getTime()) / 1000;
    if (!Number.isFinite(seconds)) return null;
    // Una marca por delante de la observación —el vencimiento de una orden, la
    // vigencia de una corrida— se lee «en 30 min», no «hace -30 min».
    if (seconds < 0) return `en ${humanSeconds(-seconds)}`;
    return `hace ${humanSeconds(seconds)}`;
}

// ---- la vigencia, que son tres palabras ----

export const VALIDITY_LABEL = {
    vigente: 'Vigente',
    vencida: 'Vencida',
    no_vence: 'No vence',
};

export const VALIDITY_TONE = {
    vigente: 'active',
    vencida: 'warn',
    no_vence: 'neutral',
};

// ---- los nombres de la coordinación ----

export const KIND_LABEL = {
    permisos_del_proveedor: 'Permisos del proveedor',
    limite_de_uso: 'Límite de uso',
    revision_incompleta: 'Revisión incompleta',
    reserva_perdida: 'Reserva perdida',
    error_recuperable: 'Error recuperable',
    equipo_ausente: 'Equipo ausente',
};

export const VERB_LABEL = {
    habilitar: 'Habilitar',
    pausar_tomas: 'Pausar tomas',
    interrumpir: 'Interrumpir',
    reintentar: 'Reintentar',
};

// `interrumpir` SOLICITA. The button says «Pedir que termine» and not «Matar»,
// because Mastermind does not kill processes: it writes an order and what it
// guarantees is that the run will not deliver.
export const VERB_ACTION_LABEL = {
    habilitar: 'Habilitar',
    pausar_tomas: 'Pausar tomas',
    interrumpir: 'Pedir que termine',
    reintentar: 'Reintentar',
};

export const PRESENCE_LABEL = {
    activa: 'Conectado',
    sin_conexion: 'Sin conexión',
    sin_latidos: 'Sin latidos',
};

export const WORK_LABEL = {
    sin_corrida: 'Sin tarea en ejecución',
    corriendo: 'Trabajando',
    vigencia_vencida: 'Vigencia vencida',
    reserva_vencida: 'Reserva vencida',
};

export const DESIRED_LABEL = {
    habilitado: 'Habilitado',
    pausado: 'Pausado',
};

export const TASK_STATE_LABEL = {
    proposed: 'Propuesta',
    ready: 'Lista para tomar',
    taken: 'Tomada',
    in_review: 'En revisión',
    done: 'Hecha',
    blocked: 'Bloqueada',
    discarded: 'Descartada',
};

export function label(map, value, fallback = '—') {
    if (value == null) return fallback;
    return map[value] ?? value;
}

// ---- el cuerpo de una orden ----
//
// `POST /admin/agents/{id}/orders` pide `request_id`, `verb`, `expected_revision`
// y `not_after`. La revisión es LA QUE SE LEYÓ antes de decidir: releerla justo
// antes de mandar para esquivar el conflicto destruye lo que el conflicto
// protege.
export function orderBody({
    verb,
    seatRevision,
    controlRevision,
    taskId,
    attemptId,
    reason,
    minutes = 30,
    now = new Date(),
}) {
    const body = {
        request_id: requestId(verb, seatRevision),
        verb,
        expected_revision: seatRevision,
        not_after: new Date(now.getTime() + minutes * 60 * 1000).toISOString(),
    };
    if (controlRevision != null) body.expected_control_revision = controlRevision;
    if (taskId != null) body.task_id = taskId;
    if (attemptId != null) body.attempt_id = attemptId;
    if (reason) body.reason = reason;
    return body;
}

// The receipt: the same key for the same intent, so a retry returns the same
// order instead of writing a second one. Pattern from the contract:
// `^[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$`.
function requestId(verb, revision) {
    return `panel.${verb}.${revision ?? 0}.${crypto.randomUUID()}`;
}
